import useAuth from "@hooks/Auth/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { Navigate } from 'react-router-dom';
import { useEffect, useRef } from "react";
import { isEmpty } from 'lodash';
import { PATH } from "config/index";

const REFRESH_LEAD_TIME_MS = 60 * 1000;

const resolveExpiryMs = (
  tokenExpires: unknown,
  storedExp: string | null,
) => {
  if (typeof tokenExpires === 'bigint') {
    return Number(tokenExpires);
  }

  if (typeof tokenExpires === 'number' && Number.isFinite(tokenExpires)) {
    return tokenExpires;
  }

  if (typeof tokenExpires === 'string' && tokenExpires.trim() !== '') {
    const parsed = Number(tokenExpires);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  if (storedExp) {
    const parsed = Number(storedExp);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

export default function PrivateRoute({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const { accessToken, tokenExpires, refreshTokens } = useAuth();
  const navigate = useNavigate();
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    const exp = localStorage.getItem('exp');
    const expiryMs = resolveExpiryMs(tokenExpires, exp);
    const triggerRefresh = async () => {
      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;

      try {
        await refreshTokens();
      } finally {
        refreshInFlightRef.current = false;
      }
    };

    if (isEmpty(accessToken)) {
      triggerRefresh();
      return;
    }

    if (!expiryMs) {
      triggerRefresh();
      return;
    }

    if (Date.now() > expiryMs) {
      triggerRefresh();
      navigate('/');
      return;
    }

    const handleMouseMove = () => {
      if (Date.now() >= expiryMs - REFRESH_LEAD_TIME_MS) {
        triggerRefresh();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [location, accessToken, tokenExpires, refreshTokens, navigate]);

  if (!accessToken) {
    navigate('/');
    return <Navigate to={PATH.LOGIN} replace />;
  }

  return <>{children}</>
}
